import PriceHistoryChart from "../../components/PriceHistoryChart";
import {
  cardStyle,
  ghostButtonStyle,
  inputStyle,
  primaryButtonStyle,
  SectionTitle,
} from "../dashboardStyles";
import {
  formatCurrency,
  formatDistance,
  getPermissionBadgeStyle,
} from "../dashboardUtils";

function Chip({ active = false, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: active ? "1px solid #f97316" : "1px solid #fdba74",
        background: active ? "#fff7ed" : "#ffffff",
        color: active ? "#c2410c" : "#7c2d12",
        borderRadius: 999,
        padding: "9px 14px",
        cursor: "pointer",
        fontWeight: 700,
      }}
    >
      {label}
    </button>
  );
}

function CompactStat({ label, value, tone = "#c2410c" }) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 18,
        border: "1px solid #fed7aa",
        background: "#fffdf8",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 800, color: "#9a3412", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: tone }}>{value}</div>
    </div>
  );
}

export default function SearchPage({
  alertMaxDistanceKm,
  alertTargetPrice,
  createPriceAlert,
  configuredDiscoveryRadiusKmLabel,
  distanceUnitLabel = "km",
  isSearchingProducts,
  isSearchingSuggestions,
  openMarketPage,
  openProductInsight,
  searchQuery,
  searchSuggestions,
  selectSearchSuggestion,
  selectedBrandFilter,
  selectedCategoryFilter,
  selectedProductInsight,
  setAlertMaxDistanceKm,
  setAlertTargetPrice,
  setBrandFilter,
  setCategoryFilter,
  setSearchQuery,
  submitSearch,
  t,
  toggleFavorite,
  unifiedSearchResult,
}) {
  const hasQuery = Boolean(searchQuery.trim());
  const markets = unifiedSearchResult?.markets || [];
  const products = (unifiedSearchResult?.products || []).filter((item) => {
    const categoryOk = !selectedCategoryFilter || item.category === selectedCategoryFilter;
    const brandOk = !selectedBrandFilter || item.brand === selectedBrandFilter;
    return categoryOk && brandOk;
  });
  const chartPoints = selectedProductInsight?.markets?.[0]?.history || [];
  const visibleMarkets = markets.slice(0, 6);
  const visibleProducts = products.slice(0, 6);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ ...cardStyle, padding: 24 }}>
        <SectionTitle
          eyebrow={t?.("tab.search", "Buscar") || "Buscar"}
          title={
            t?.("search.title", "Procure mercado, categoria, marca ou produto") ||
            "Procure mercado, categoria, marca ou produto"
          }
          description={
            t?.(
              "search.description",
              "Use um unico campo para encontrar mais rapido o mercado certo ou o menor preco."
            ) || "Use um unico campo para encontrar mais rapido o mercado certo ou o menor preco."
          }
        />

        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitSearch();
          }}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
          }}
          className="stack-tight"
        >
          <div style={{ display: "grid", gap: 10 }}>
            <input
              style={{
                ...inputStyle,
                minHeight: 58,
                borderRadius: 999,
                paddingInline: 20,
                fontSize: 16,
              }}
              placeholder={
                t?.("search.placeholder", "Buscar por mercado, farmacia, arroz, marca, categoria...") ||
                "Buscar por mercado, farmacia, arroz, marca, categoria..."
              }
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {(isSearchingSuggestions || searchSuggestions?.length > 0) && (
              <div
                style={{
                  display: "grid",
                  gap: 8,
                  padding: 12,
                  borderRadius: 18,
                  border: "1px solid #fed7aa",
                  background: "#ffffff",
                }}
              >
                <strong style={{ fontSize: 13, color: "#7c2d12" }}>
                  {t?.("search.suggestions", "Sugestoes") || "Sugestoes"}
                </strong>
                {isSearchingSuggestions && (
                  <div style={{ color: "#9a3412" }}>
                    {t?.("search.suggestions.loading", "Buscando recomendacoes...") ||
                      "Buscando recomendacoes..."}
                  </div>
                )}
                {searchSuggestions?.map((suggestion) => (
                  <button
                    key={suggestion.key}
                    type="button"
                    onClick={() => selectSearchSuggestion(suggestion)}
                    style={{
                      textAlign: "left",
                      padding: 14,
                      borderRadius: 16,
                      border: "1px solid #ffedd5",
                      background: "#fffaf5",
                      cursor: "pointer",
                    }}
                  >
                    <strong style={{ display: "block" }}>{suggestion.title}</strong>
                    <div style={{ marginTop: 6, color: "#9a3412" }}>
                      {(suggestion.kind === "market"
                        ? t?.("search.suggestion.market", "Mercado")
                        : t?.("search.suggestion.product", "Produto")) || suggestion.kind}
                      {suggestion.subtitle ? ` | ${suggestion.subtitle}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" style={primaryButtonStyle} disabled={isSearchingProducts}>
            {isSearchingProducts
              ? t?.("search.loading", "Buscando...") || "Buscando..."
              : t?.("search.button", "Buscar") || "Buscar"}
          </button>
        </form>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
            marginTop: 16,
          }}
        >
          <CompactStat
            label={t?.("search.stats.markets", "Mercados") || "Mercados"}
            value={markets.length}
          />
          <CompactStat
            label={t?.("search.stats.products", "Produtos") || "Produtos"}
            value={products.length}
          />
          <CompactStat
            label={t?.("search.stats.radius", "Raio") || "Raio"}
            value={configuredDiscoveryRadiusKmLabel}
            tone="#ea580c"
          />
        </div>

        {(unifiedSearchResult?.categories?.length > 0 || unifiedSearchResult?.brands?.length > 0) && (
          <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
            {unifiedSearchResult.categories?.length > 0 && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Chip
                  label={t?.("search.filters.all_categories", "Todas categorias") || "Todas categorias"}
                  active={!selectedCategoryFilter}
                  onClick={() => setCategoryFilter("")}
                />
                {unifiedSearchResult.categories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    active={selectedCategoryFilter === category}
                    onClick={() => setCategoryFilter(category)}
                  />
                ))}
              </div>
            )}
            {unifiedSearchResult.brands?.length > 0 && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Chip
                  label={t?.("search.filters.all_brands", "Todas marcas") || "Todas marcas"}
                  active={!selectedBrandFilter}
                  onClick={() => setBrandFilter("")}
                />
                {unifiedSearchResult.brands.map((brand) => (
                  <Chip
                    key={brand}
                    label={brand}
                    active={selectedBrandFilter === brand}
                    onClick={() => setBrandFilter(brand)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1.02fr 0.98fr", gap: 20 }}
        className="stack-on-mobile"
      >
        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("search.markets.eyebrow", "Mercados") || "Mercados"}
            title={
              hasQuery
                ? t?.("search.markets.title.with_query", "Mercados encontrados") || "Mercados encontrados"
                : t?.("search.markets.title.empty", "Mercados para explorar") || "Mercados para explorar"
            }
            description={
              hasQuery
                ? t?.("search.markets.description.with_query", "Veja primeiro os locais mais relevantes e proximos da sua busca.") ||
                  "Veja primeiro os locais mais relevantes e proximos da sua busca."
                : t?.("search.markets.description.empty", "Os mercados mais relevantes aparecem aqui assim que voce buscar.") ||
                  "Os mercados mais relevantes aparecem aqui assim que voce buscar."
            }
          />

          <div style={{ display: "grid", gap: 10 }}>
            {visibleMarkets.length > 0 ? (
              visibleMarkets.map((market) => (
                <div
                  key={market.market_id}
                  style={{
                    padding: 16,
                    borderRadius: 20,
                    border: "1px solid #fed7aa",
                    background: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <strong>{market.market}</strong>
                    <span style={getPermissionBadgeStyle("prompt")}>
                      {formatDistance(market.distance_km)}
                    </span>
                  </div>
                  <div style={{ marginTop: 8, color: "#7c2d12" }}>
                    {market.address ||
                      t?.("search.markets.address_missing", "Endereco ainda nao informado") ||
                      "Endereco ainda nao informado"}
                  </div>
                  <div style={{ marginTop: 8, color: "#9a3412" }}>
                    {market.category || t?.("search.markets.local", "local") || "local"}
                    {market.neighborhood ? ` | ${market.neighborhood}` : ""}
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                    <button
                      onClick={() =>
                        openMarketPage(market, {
                          statusMessage:
                            t?.("status.market.open.search", "{{market}} aberto pela busca.", {
                              market: market.market,
                            }) || `${market.market} aberto pela busca.`,
                        })
                      }
                      style={ghostButtonStyle}
                    >
                      {t?.("search.markets.open", "Abrir mercado") || "Abrir mercado"}
                    </button>
                    <button
                      onClick={() =>
                        toggleFavorite({
                          entity_type: "market",
                          entity_id: market.market_id,
                          title: market.market,
                          subtitle: market.address,
                        })
                      }
                      style={ghostButtonStyle}
                    >
                      {market.favorite
                        ? t?.("common.remove_favorite", "Remover favorito") || "Remover favorito"
                        : t?.("common.favorite", "Favoritar") || "Favoritar"}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: "#9a3412" }}>
                {hasQuery
                  ? t?.("search.markets.no_results", "Nenhum mercado encontrado com os filtros atuais.") ||
                    "Nenhum mercado encontrado com os filtros atuais."
                  : t?.("search.markets.empty", "Assim que os mercados forem carregados, eles aparecem aqui.") ||
                    "Assim que os mercados forem carregados, eles aparecem aqui."}
              </div>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("search.products.eyebrow", "Produtos") || "Produtos"}
            title={t?.("search.products.title", "Resultados do catalogo") || "Resultados do catalogo"}
            description={
              t?.("search.products.description", "Veja primeiro os itens com melhor oportunidade para comparar mais rapido.") ||
              "Veja primeiro os itens com melhor oportunidade para comparar mais rapido."
            }
          />

          {visibleProducts.length > 0 ? (
            <div style={{ display: "grid", gap: 10 }}>
              {visibleProducts.map((item, index) => (
                <div
                  key={item.product_id}
                  style={{
                    padding: 16,
                    borderRadius: 20,
                    border: "1px solid #fed7aa",
                    background: index === 0 ? "#fff7ed" : "#ffffff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <strong>{item.product}</strong>
                      <div style={{ marginTop: 6, color: "#9a3412" }}>
                        {[item.brand, item.category].filter(Boolean).join(" | ") ||
                          t?.("search.products.uncategorized", "sem categoria") ||
                          "sem categoria"}
                      </div>
                    </div>
                    <span style={getPermissionBadgeStyle(index === 0 ? "granted" : "prompt")}>
                      {item.price_mode === "club"
                        ? t?.("search.products.club_price", "preco clube") || "preco clube"
                        : item.price_mode}
                    </span>
                  </div>
                  <div style={{ marginTop: 8, color: "#ef4444", fontWeight: 900, fontSize: 24 }}>
                    {formatCurrency(item.best_price)}
                  </div>
                  <div style={{ marginTop: 8, color: "#7c2d12" }}>
                    {(t?.("search.products.best_market", "Melhor mercado atual") ||
                      "Melhor mercado atual") + ": "}
                    {item.market || (t?.("common.no_data", "n/d") || "n/d")}
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                    <button onClick={() => openProductInsight(item.product_id)} style={ghostButtonStyle}>
                      {t?.("search.products.open", "Ir para pagina do produto") ||
                        "Ir para pagina do produto"}
                    </button>
                    <button
                      onClick={() =>
                        toggleFavorite({
                          entity_type: "product",
                          entity_id: item.product_id,
                          title: item.product,
                          subtitle: item.brand || item.category || "",
                        })
                      }
                      style={ghostButtonStyle}
                    >
                      {item.favorite
                        ? t?.("common.remove_favorite", "Remover favorito") || "Remover favorito"
                        : t?.("common.favorite", "Favoritar") || "Favoritar"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#9a3412" }}>
              {hasQuery
                ? t?.("search.products.no_results", "Busque para encontrar produtos correspondentes.") ||
                  "Busque para encontrar produtos correspondentes."
                : t?.("search.products.empty", "Use a barra acima para procurar um produto pelo nome.") ||
                  "Use a barra acima para procurar um produto pelo nome."}
            </div>
          )}
        </div>
      </div>

      <div style={cardStyle}>
        <SectionTitle
          eyebrow={t?.("search.product_page.eyebrow", "Pagina do produto") || "Pagina do produto"}
          title={
            selectedProductInsight?.product ||
            (t?.("search.product_page.title.empty", "Selecione um produto da busca") ||
              "Selecione um produto da busca")
          }
          description={
            selectedProductInsight
              ? t?.("search.product_page.description.selected", "Veja historico, menor preco e crie um alerta rapido.") ||
                "Veja historico, menor preco e crie um alerta rapido."
              : t?.("search.product_page.description.empty", "Escolha um item da busca para abrir o painel detalhado.") ||
                "Escolha um item da busca para abrir o painel detalhado."
          }
        />

        {selectedProductInsight ? (
          <div style={{ display: "grid", gap: 18 }}>
            <div
              style={{
                padding: 18,
                borderRadius: 22,
                background: "linear-gradient(135deg, #fff7ed 0%, #fffaf5 100%)",
                border: "1px solid #fed7aa",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 12,
                  alignItems: "start",
                }}
                className="stack-tight"
              >
                <div>
                  <strong style={{ display: "block", fontSize: 22 }}>
                    {selectedProductInsight.product}
                  </strong>
                  <div style={{ marginTop: 8, color: "#7c2d12" }}>
                    {(t?.("search.product_page.best_market", "Melhor mercado agora") ||
                      "Melhor mercado agora") + ": "}
                    {selectedProductInsight.best_market || (t?.("common.no_data", "n/d") || "n/d")}
                  </div>
                </div>
                <button
                  onClick={() =>
                    toggleFavorite({
                      entity_type: "product",
                      entity_id: selectedProductInsight.product_id,
                      title: selectedProductInsight.product,
                      subtitle: selectedProductInsight.brand || selectedProductInsight.category || "",
                    })
                  }
                  style={ghostButtonStyle}
                >
                  {selectedProductInsight.favorite
                    ? t?.("common.remove_favorite", "Remover favorito") || "Remover favorito"
                    : t?.("common.favorite", "Favoritar") || "Favoritar"}
                </button>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
                <input
                  style={{ ...inputStyle, minWidth: 160 }}
                  placeholder={t?.("search.product_page.alert_placeholder", "Alvo ex: 19,90") || "Alvo ex: 19,90"}
                  value={alertTargetPrice}
                  onChange={(event) => setAlertTargetPrice(event.target.value)}
                />
                <input
                  style={{ ...inputStyle, minWidth: 180 }}
                  placeholder={
                    t?.(
                      "search.product_page.alert_radius_placeholder",
                      `Raio maximo (${distanceUnitLabel})`
                    ) || `Raio maximo (${distanceUnitLabel})`
                  }
                  value={alertMaxDistanceKm}
                  onChange={(event) => setAlertMaxDistanceKm(event.target.value)}
                />
                <button onClick={createPriceAlert} style={primaryButtonStyle}>
                  {t?.("search.product_page.create_alert", "Criar alerta") || "Criar alerta"}
                </button>
              </div>
            </div>

            <div style={{ color: "#9a3412" }}>
              {(
                t?.(
                  "search.product_page.alert_scope",
                  "O alerta monitora qualquer mercado dentro do raio salvo para sua localizacao."
                ) || "O alerta monitora qualquer mercado dentro do raio salvo para sua localizacao."
              ) + " "}
              {(t?.("search.product_page.alert_current_radius", "Padrao atual") || "Padrao atual") + ": "}
              {configuredDiscoveryRadiusKmLabel}.
            </div>

            <PriceHistoryChart points={chartPoints} t={t} />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                gap: 14,
              }}
            >
              {selectedProductInsight.markets.slice(0, 4).map((market, index) => (
                <div
                  key={`${selectedProductInsight.product_id}-${market.market_id}`}
                  style={{
                    padding: 16,
                    borderRadius: 20,
                    border: "1px solid #fed7aa",
                    background: index === 0 ? "#fff7ed" : "#ffffff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <strong>{market.market}</strong>
                    <span style={getPermissionBadgeStyle(index === 0 ? "granted" : "prompt")}>
                      {formatCurrency(market.latest_price)}
                    </span>
                  </div>
                  <div style={{ marginTop: 8, color: "#7c2d12" }}>
                    {(t?.("search.product_page.lowest_price", "Menor preco") || "Menor preco") + ": "}
                    {formatCurrency(market.lowest_price)}
                  </div>
                  <div style={{ marginTop: 6, color: "#7c2d12" }}>
                    {(t?.("search.ranking.average", "Media") || "Media") + ": "}
                    {formatCurrency(market.average_price)}
                  </div>
                  <div style={{ marginTop: 6, color: "#9a3412" }}>
                    {t?.(
                      "search.product_page.market_meta",
                      "Distancia: {{distance}} | confiabilidade {{confidence}}",
                      {
                        distance: formatDistance(market.distance_km),
                        confidence: market.confidence_label || (t?.("common.no_data", "n/d") || "n/d"),
                      }
                    ) || ""}
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                    <button
                      onClick={() =>
                        openMarketPage(
                          {
                            market_id: market.market_id,
                            market: market.market,
                            address: market.address,
                            distance_km: market.distance_km,
                          },
                          {
                            statusMessage:
                              t?.(
                                "status.market.open.product_page",
                                "{{market}} aberto pela pagina do produto.",
                                {
                                  market: market.market,
                                }
                              ) || `${market.market} aberto pela pagina do produto.`,
                          }
                        )
                      }
                      style={ghostButtonStyle}
                    >
                      {t?.("search.markets.open", "Abrir mercado") || "Abrir mercado"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ color: "#9a3412" }}>
            {t?.("search.product_page.empty", "Busque um produto acima para abrir a pagina dele com historico e alertas.") ||
              "Busque um produto acima para abrir a pagina dele com historico e alertas."}
          </div>
        )}
      </div>
    </div>
  );
}
